import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {SequencePreferences} from 'constants/SequenceConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {withoutSupersededAaSequenceNode} from 'graphql-client/WithoutSupersededAaSequenceNode';
import {
  BiointelligentLinkContainerCountQuery,
  BiointelligentLinkContainerCountQueryVariables,
} from 'molbio/biointelligent-links/__generated/BiointelligentLinkContainerCountQuery';
import {BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY} from 'molbio/biointelligent-links/BiointelligentLinkQueries';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import usePreference from 'ui-library/hooks/usePreference';

function PartsWarningBanner({sequenceId}: {sequenceId: string}): JSX.Element {
  const [collapsed, setCollapsed] = usePreference<boolean>(SequencePreferences.SHOULD_COLLAPSE_PARTS_WARNING);
  return (
    <BenchlingQuery<BiointelligentLinkContainerCountQuery, BiointelligentLinkContainerCountQueryVariables>
      query={BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY}
      variables={{id: sequenceId}}
      renderLoading={() => <div />}
    >
      {(data: BiointelligentLinkContainerCountQuery) => {
        const polymer = withoutSupersededAaSequenceNode(data.polymer);
        if (!isInaccessible(polymer)) {
          const numContainingSequences = polymer.partContainerCount;
          if (numContainingSequences === 0) {
            return <div />;
          }
          const demonstrativeText = pluralize(numContainingSequences, 'that', 'those');
          const sequenceText = pluralize(numContainingSequences, 'sequence');
          const supportingInfo =
            BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS || BenchlingConfig.PARTS_ENABLE_CROSS_ORIGIN_PARTS
              ? 'Base edits are not allowed on sequences that are contained by other sequences.' +
                ' To change bases, please make a copy of this sequence instead, or unlink it from the containing' +
                ` ${pluralize(numContainingSequences, 'sequence')}.`
              : `Editing this sequence will change ${demonstrativeText} ${numContainingSequences} containing ` +
                `${sequenceText}, and any additional sequences that reference ${demonstrativeText} ` +
                `containing ${sequenceText} as ${pluralize(numContainingSequences, 'a part', 'parts')}.`;

          const message = `This sequence is referenced as a part in ${numContainingSequences} containing ${sequenceText}.`;
          return (
            <div className={css(styles.banner)}>
              <InlineBanner
                bannerType={InlineBannerType.INFO}
                text={message}
                supportingInfo={supportingInfo}
                action={{
                  actionType: InlineBannerActionType.ACCORDION,
                  initialShowMore: !collapsed,
                  onClickAccordionButton: (newIsExpanded: boolean) => {
                    setCollapsed(!newIsExpanded);
                  },
                }}
              />
            </div>
          );
        }
        return <div />;
      }}
    </BenchlingQuery>
  );
}

export default PartsWarningBanner;

const styles = StyleSheet.create({
  banner: {
    margin: '4px',
  },
});
